import {
  RelatorioUnidadePdfItem,
  RelatorioUnidadesFields,
} from "features/relatorios/typings";
import { formatPhone } from "infra/helpers/number";
import { useMemo } from "react";
import { PDFTableColumn } from "components/PDF/Table";
import { View, Text } from "@react-pdf/renderer";
import { formatCNPJ, formatCPF } from "@brazilian-utils/brazilian-utils";
import { TipoContatoEnum } from "core/features/auth/typings/user";

function useRelatorioUnidadesPdfColumns(
  includedFields?: RelatorioUnidadesFields[]
): PDFTableColumn<RelatorioUnidadePdfItem>[] {
  return useMemo(() => {
    const columns: PDFTableColumn<RelatorioUnidadePdfItem>[] = [];

    columns.push({
      title: "Unidade",
      flexGrow: 0.6,
      render: (original) => (
        <View>
          <Text>Unidade {original.unidade}</Text>
        </View>
      ),
    });

    if (includedFields?.includes("fracao")) {
      columns.push({
        title: "Fração",
        flexGrow: 0.25,
        render: (original) => (
          <View>
            <Text>{original.fracao}</Text>
          </View>
        ),
      });
    }

    if (includedFields?.includes("peso")) {
      columns.push({
        title: "Peso",
        flexGrow: 0.25,
        render: (original) => (
          <View>
            <Text>{original.peso}</Text>
          </View>
        ),
      });
    }

    if (includedFields?.includes("area")) {
      columns.push({
        title: "Área privativa",
        flexGrow: 0.25,
        render: (original) => (
          <View>
            <Text>{original.area} m²</Text>
          </View>
        ),
      });
    }

    if (includedFields?.includes("areaConstruida")) {
      columns.push({
        title: "Área construída",
        flexGrow: 0.35,
        render: (original) => (
          <View>
            <Text>{original.area} m²</Text>
          </View>
        ),
      });
    }

    columns.push({
      title: "Nome",
      flexGrow: 0.8,
      render: (original) => (
        <View>
          <Text>{original.pessoa.nome}</Text>
        </View>
      ),
    });

    if (includedFields?.includes("documento")) {
      columns.push({
        title: "CPF/CNPJ",
        flexGrow: 0.6,
        render: (original) => (
          <View>
            {original.pessoa?.documento ? (
              <Text>
                {original.pessoa?.documento?.length > 11
                  ? formatCNPJ(original.pessoa.documento)
                  : formatCPF(original.pessoa.documento)}
              </Text>
            ) : null}
          </View>
        ),
      });
    }

    if (includedFields?.includes("contatos")) {
      columns.push({
        title: "Telefone/Celular",
        flexGrow: 0.6,
        render: (original) => (
          <View>
            {original.pessoa.contatos
              .filter((contato) =>
                [TipoContatoEnum.Celular, TipoContatoEnum.Telefone].includes(
                  contato.tipoContatoId
                )
              )
              .map((contato) => (
                <Text key={contato.id}>{formatPhone(contato.conteudo)}</Text>
              ))}
          </View>
        ),
      });

      columns.push({
        title: "Email",
        flexGrow: 0.8,
        render: (original) => (
          <View>
            {original.pessoa.contatos
              .filter((contato) =>
                [TipoContatoEnum.Email].includes(contato.tipoContatoId)
              )
              .map((contato) => (
                <Text key={contato.id}>{contato.conteudo}</Text>
              ))}
          </View>
        ),
      });
    }

    if (includedFields?.includes("tipoUnidadePessoaId")) {
      columns.push({
        title: "Tipo",
        flexGrow: 0.45,
        render: (original) => (
          <View>
            <Text>{original.pessoa.tipoUnidadePessoaNome}</Text>
          </View>
        ),
      });
    }

    if (includedFields?.includes("endereco")) {
      columns.push({
        title: "Endereço",
        flexGrow: 1,
        render: (original) => (
          <View>
            <Text>{original.pessoa.endereco}</Text>
          </View>
        ),
      });
    }

    return columns;
  }, [includedFields]);
}

export default useRelatorioUnidadesPdfColumns;
